import React, { useState, useEffect, useCallback } from "react";
import api from "@/services/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Sunrise, Copy, Loader2, Save, CheckCircle2, AlertTriangle, Trash2 } from "lucide-react";

const ESTADOS = {
  sin_asignar: { l: "Sin asignar", c: "bg-gray-100 text-gray-600" },
  asignado: { l: "Asignado · esperando vendedor", c: "bg-blue-100 text-blue-800" },
  confirmado: { l: "✓ Confirmado y cargado", c: "bg-green-100 text-green-800" },
  en_revision: { l: "⚠️ Diferencias · requiere aprobación", c: "bg-amber-100 text-amber-800" },
  aprobado: { l: "✓ Aprobado y cargado", c: "bg-green-100 text-green-800" },
  rechazado: { l: "Rechazado", c: "bg-red-100 text-red-700" },
};

export default function AdminAperturas() {
  const [fecha, setFecha] = useState(new Date(Date.now() - 5 * 3600000).toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [productos, setProductos] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [guardando, setGuardando] = useState("");
  const [copiando, setCopiando] = useState(false);

  const cargar = useCallback((f) => {
    api.get(`/inventario/turnos?fecha=${f}`).then((r) => {
      setData(r.data);
      const d = {};
      r.data.turnos.forEach((t) => {
        d[t.vendedor_id] = {
          conservar_saldo: t.conservar_saldo,
          items: Object.fromEntries(t.items.map((i) => [i.producto_id, i.cantidad_asignada])),
        };
      });
      setDrafts(d);
    }).catch(() => toast.error("Error cargando turnos"));
  }, []);

  useEffect(() => {
    api.get("/productos").then((r) => setProductos(r.data.filter((p) => p.activo)));
  }, []);

  useEffect(() => { cargar(fecha); }, [fecha, cargar]);

  const setCantidad = (vid, pid, val) => {
    setDrafts((prev) => ({
      ...prev,
      [vid]: { ...(prev[vid] || { conservar_saldo: false, items: {} }), items: { ...(prev[vid]?.items || {}), [pid]: val } },
    }));
  };

  const setConservar = (vid, val) => {
    setDrafts((prev) => ({ ...prev, [vid]: { ...(prev[vid] || { items: {} }), conservar_saldo: val } }));
  };

  const guardar = async (vid) => {
    const draft = drafts[vid] || { items: {}, conservar_saldo: false };
    const items = Object.entries(draft.items)
      .map(([producto_id, cantidad]) => ({ producto_id, cantidad: parseInt(cantidad) || 0 }))
      .filter((i) => i.cantidad > 0);
    if (items.length === 0) { toast.error("Ingresa al menos una cantidad"); return; }
    setGuardando(vid);
    try {
      await api.post("/inventario/turnos/asignar", { fecha, vendedor_id: vid, items, conservar_saldo: draft.conservar_saldo });
      toast.success("Asignación guardada y notificada al vendedor");
      cargar(fecha);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando");
    } finally {
      setGuardando("");
    }
  };

  const copiarAnterior = async () => {
    setCopiando(true);
    try {
      const r = await api.post("/inventario/turnos/copiar-anterior", { fecha });
      toast.success(`Copiadas ${r.data.copiados} asignaciones del ${r.data.fecha_origen}${r.data.omitidos ? ` (${r.data.omitidos} omitidas)` : ""}`);
      cargar(fecha);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error copiando");
    } finally {
      setCopiando(false);
    }
  };

  const aprobar = async (turnoId) => {
    try {
      await api.post(`/inventario/turnos/${turnoId}/aprobar`, {});
      toast.success("Apertura aprobada y cargada");
      cargar(fecha);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error aprobando");
    }
  };

  const eliminar = async (turnoId) => {
    try {
      await api.delete(`/inventario/turnos/${turnoId}`);
      toast.success("Asignación eliminada");
      cargar(fecha);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error eliminando");
    }
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-torta-brown" /></div>;

  const turnosPorVendedor = Object.fromEntries(data.turnos.map((t) => [t.vendedor_id, t]));

  return (
    <div className="space-y-6" data-testid="admin-aperturas-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl text-torta-dark flex items-center gap-2"><Sunrise className="w-7 h-7" /> Apertura Diaria</h1>
          <p className="text-torta-muted text-sm">Asigna el inventario del día a tus vendedores de carrito en un solo paso</p>
        </div>
        <div className="flex gap-2 items-center">
          <Input type="date" data-testid="fecha-aperturas" value={fecha} onChange={(e) => setFecha(e.target.value)} className="w-40" />
          <Button variant="outline" data-testid="copiar-anterior-btn" onClick={copiarAnterior} disabled={copiando}>
            {copiando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Copy className="w-4 h-4 mr-1" />} Copiar día anterior
          </Button>
        </div>
      </div>

      {data.vendedores_carrito.length === 0 && (
        <p className="text-center py-10 text-torta-muted bg-white rounded-2xl">No hay vendedores de carrito activos.</p>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {data.vendedores_carrito.map((v) => {
          const turno = turnosPorVendedor[v.id];
          const estado = turno?.estado || "sin_asignar";
          const est = ESTADOS[estado];
          const draft = drafts[v.id] || { items: {}, conservar_saldo: false };
          const editable = !turno || ["asignado", "rechazado"].includes(estado);
          const totalAsignado = Object.values(draft.items).reduce((s, x) => s + (parseInt(x) || 0), 0);
          return (
            <div key={v.id} className="bg-white rounded-2xl p-5 shadow-sm space-y-3" data-testid={`apertura-card-${v.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <p className="font-semibold text-torta-dark">{v.nombre}</p>
                  <p className="text-xs text-torta-muted">{v.zona || "Sin zona"}</p>
                </div>
                <Badge className={`border-0 ${est.c}`} data-testid={`estado-turno-${v.id}`}>{est.l}</Badge>
              </div>

              {estado === "en_revision" && turno && (
                <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 space-y-1.5" data-testid={`diferencias-${v.id}`}>
                  <p className="text-xs font-semibold text-amber-800 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Diferencias reportadas:</p>
                  {turno.items.filter((i) => i.cantidad_recibida !== i.cantidad_asignada).map((i) => (
                    <p key={i.producto_id} className="text-xs text-amber-900">{i.producto_nombre}: asignado <strong>{i.cantidad_asignada}</strong> → recibió <strong>{i.cantidad_recibida}</strong></p>
                  ))}
                  {turno.notas_vendedor && <p className="text-xs text-amber-700 italic">"{turno.notas_vendedor}"</p>}
                  <Button size="sm" data-testid={`aprobar-turno-${v.id}`} onClick={() => aprobar(turno.id)} className="bg-green-600 hover:bg-green-700 mt-1">
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Aprobar con lo recibido
                  </Button>
                </div>
              )}

              {["confirmado", "aprobado"].includes(estado) && turno ? (
                <div className="space-y-1">
                  {turno.items.map((i) => (
                    <div key={i.producto_id} className="flex justify-between text-sm py-1 border-b last:border-0 border-torta-sand/50">
                      <span className="text-torta-dark">{i.producto_nombre}</span>
                      <span className="font-bold text-green-700">{i.cantidad_aprobada ?? i.cantidad_recibida ?? i.cantidad_asignada} und</span>
                    </div>
                  ))}
                </div>
              ) : estado !== "en_revision" && (
                <>
                  <div className="space-y-1.5">
                    {productos.map((p) => (
                      <div key={p.id} className="flex items-center justify-between gap-2">
                        <span className="text-sm text-torta-dark flex-1">{p.nombre}</span>
                        <Input
                          type="number" min="0"
                          data-testid={`cantidad-${v.id}-${p.id}`}
                          className="w-20 h-8 text-center"
                          value={draft.items[p.id] ?? ""}
                          placeholder="0"
                          disabled={!editable}
                          onChange={(e) => setCantidad(v.id, p.id, e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between pt-1">
                    <div className="flex items-center gap-2">
                      <Switch checked={draft.conservar_saldo} disabled={!editable} onCheckedChange={(val) => setConservar(v.id, val)} />
                      <span className="text-xs text-torta-muted">Conservar saldo anterior</span>
                    </div>
                    <p className="text-xs font-semibold text-torta-dark">{totalAsignado} und</p>
                  </div>
                  {editable && (
                    <div className="flex gap-2">
                      <Button data-testid={`guardar-turno-${v.id}`} onClick={() => guardar(v.id)} disabled={guardando === v.id} className="flex-1 bg-torta-brown hover:bg-torta-brown/90">
                        {guardando === v.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                        {turno ? "Actualizar" : "Asignar"}
                      </Button>
                      {turno && (
                        <Button variant="outline" size="icon" onClick={() => eliminar(turno.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
